import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import ImageCarousel from '../../components/shared/ImageCarousel';
import useScrollToTop from '../../hooks/useScrollToTop';

const TwinLakes: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const images = [
    { src: '/images/tlvc4-1.jpg', alt: 'Twin Lakes Villa Community 1' },
    { src: '/images/tlvc4-2.jpg', alt: 'Twin Lakes Villa Community 2' },
    { src: '/images/tlvc4-3.jpg', alt: 'Twin Lakes Villa Community 3' },
    { src: '/images/tlvc4-4.jpg', alt: 'Twin Lakes Villa Community 4' },
    { src: '/images/tlvc4-5.jpg', alt: 'Twin Lakes Villa Community 5' },
    { src: '/images/tlvc4-6.jpg', alt: 'Twin Lakes Villa Community 6' },
    { src: '/images/tlvc4-7.jpg', alt: 'Twin Lakes Villa Community 7' },
    { src: '/images/tlvc4-8.jpg', alt: 'Twin Lakes Villa Community 8' },
    { src: '/images/tlvc4-9.jpg', alt: 'Twin Lakes Villa Community 9' },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#1976d2',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('projects.twinlakes.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('projects.twinlakes.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Project Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom>
                {t('projects.twinlakes.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('projects.twinlakes.overview.paragraph1')}
              </Typography>
              <Typography variant="body1" paragraph>
                {t('projects.twinlakes.overview.paragraph2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Immigration Benefits */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h4" gutterBottom>
            {t('projects.twinlakes.benefits.title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.twinlakes.benefits.paragraph1')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.twinlakes.benefits.paragraph2')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.twinlakes.benefits.paragraph3')}
          </Typography>
        </Container>
      </Box>

      {/* Financial Security */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h4" gutterBottom>
          {t('projects.twinlakes.financial.title')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('projects.twinlakes.financial.paragraph1')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('projects.twinlakes.financial.paragraph2')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('projects.twinlakes.financial.paragraph3')}
        </Typography>
      </Container>

      {/* Developer Background */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h4" gutterBottom>
            {t('projects.twinlakes.developer.title')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.twinlakes.developer.paragraph1')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.twinlakes.developer.paragraph2')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('projects.twinlakes.developer.paragraph3')}
          </Typography>
        </Container>
      </Box>

      {/* Image Carousel */}
      <Box sx={{ py: 4 }}>
        <Container>
          <ImageCarousel images={images} />
        </Container>
      </Box>

      {/* Back to EB5 Button */}
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Container>
          <Button
            component={Link}
            to="/us-green-card/eb5"
            variant="contained"
            color="primary"
            size="large"
          >
            {t('projects.twinlakes.backToEB5')}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default TwinLakes; 